import React, { useState, useEffect, useCallback } from 'react';
import {
  MdError, MdWarning, MdInfo, MdBugReport, MdRefresh,
  MdSearch, MdFilterList, MdCheckCircle, MdCancel,
  MdExpandMore, MdExpandLess, MdDelete, MdDone,
  MdReplay, MdContentCopy, MdAccessTime, MdPending,
  MdOutlineErrorOutline, MdOutlineCheckCircle
} from 'react-icons/md';
import { toast } from 'react-toastify';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import { errorLogsApi } from '../../../api/errorLogsApi';
import '../Admin.css';
import './ErrorLogs.css';

const SEVERITY_CONFIG = {
  CRITICAL: { icon: MdError, color: '#dc2626', bg: '#fef2f2', label: 'Critical' },
  ERROR: { icon: MdBugReport, color: '#ea580c', bg: '#fff7ed', label: 'Error' },
  WARNING: { icon: MdWarning, color: '#ca8a04', bg: '#fefce8', label: 'Warning' },
  INFO: { icon: MdInfo, color: '#0284c7', bg: '#f0f9ff', label: 'Info' }
};

const STATUS_TABS = [
  { key: 'all', label: 'All', icon: MdOutlineErrorOutline },
  { key: 'open', label: 'Open', icon: MdCancel },
  { key: 'pending', label: 'Pending', icon: MdPending },
  { key: 'resolved', label: 'Resolved', icon: MdOutlineCheckCircle }
];

function ErrorLogs() {
  const [errors, setErrors] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDashboardLoading, setIsDashboardLoading] = useState(true);

  // Active status tab
  const [activeTab, setActiveTab] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filters
  const [filters, setFilters] = useState({
    severity: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selected errors for bulk actions
  const [selectedErrors, setSelectedErrors] = useState(new Set());
  const [expandedError, setExpandedError] = useState(null);

  // Modal states
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [resolvingErrorId, setResolvingErrorId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingErrorId, setDeletingErrorId] = useState(null);

  // Get resolved filter value based on active tab
  const getResolvedFilter = () => {
    switch (activeTab) {
      case 'open':
        return 'false';
      case 'resolved':
        return 'true';
      case 'pending':
        return 'pending'; // Will need backend support
      default:
        return '';
    }
  };

  // Fetch error logs with default sort by latest (timestamp desc)
  const fetchErrorLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const resolvedValue = getResolvedFilter();
      const response = await errorLogsApi.getErrorLogs({
        page: currentPage,
        size: pageSize,
        severity: filters.severity || undefined,
        resolved: resolvedValue !== '' && resolvedValue !== 'pending' ? resolvedValue === 'true' : undefined,
        search: filters.search || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy: 'timestamp',
        sortDir: 'desc' // Default sort by latest
      });

      if (response.data) {
        let filteredContent = response.data.content || [];

        // Client-side filter for pending status if needed
        if (activeTab === 'pending') {
          filteredContent = filteredContent.filter(e => e.status === 'PENDING');
        }

        setErrors(filteredContent);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('Error fetching error logs:', error);
      toast.error('Failed to fetch error logs');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, filters, activeTab]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    try {
      setIsDashboardLoading(true);
      const response = await errorLogsApi.getDashboardStats();
      if (response.data) {
        setDashboardStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setIsDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchErrorLogs();
  }, [fetchErrorLogs]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setSelectedErrors(new Set());
    setExpandedError(null);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(0);
  };

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, search: value }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (filters.search !== '') {
        setCurrentPage(0);
        fetchErrorLogs();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Handle resolve error
  const handleResolve = async () => {
    try {
      if (selectedErrors.size > 1) {
        await errorLogsApi.bulkResolve(Array.from(selectedErrors), resolveNotes);
        toast.success(`${selectedErrors.size} errors marked as resolved`);
      } else {
        await errorLogsApi.resolveError(resolvingErrorId, resolveNotes);
        toast.success('Error marked as resolved');
      }
      setShowResolveModal(false);
      setResolveNotes('');
      setResolvingErrorId(null);
      setSelectedErrors(new Set());
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to resolve error');
    }
  };

  // Handle reopen error
  const handleReopen = async (id) => {
    try {
      await errorLogsApi.reopenError(id);
      toast.success('Error reopened');
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to reopen error');
    }
  };

  // Handle delete error
  const handleDelete = async () => {
    try {
      await errorLogsApi.deleteError(deletingErrorId);
      toast.success('Error log deleted');
      setShowDeleteModal(false);
      setDeletingErrorId(null);
      fetchErrorLogs();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Failed to delete error');
    }
  };

  // Toggle error selection
  const toggleErrorSelection = (id) => {
    const newSelected = new Set(selectedErrors);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedErrors(newSelected);
  };

  // Select all errors
  const toggleSelectAll = () => {
    if (selectedErrors.size === errors.length) {
      setSelectedErrors(new Set());
    } else {
      setSelectedErrors(new Set(errors.map(e => e.id)));
    }
  };

  // Copy stack trace to clipboard
  const copyStackTrace = (stackTrace) => {
    navigator.clipboard.writeText(stackTrace);
    toast.success('Stack trace copied to clipboard');
  };

  // Format date compact
  const formatDateCompact = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' });
  };

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDateCompact(dateStr);
  };

  // Get tab count
  const getTabCount = (tab) => {
    if (!dashboardStats) return '';
    switch (tab) {
      case 'open':
        return dashboardStats.unresolvedCount || 0;
      case 'resolved':
        return dashboardStats.resolvedCount || 0;
      case 'pending':
        return dashboardStats.pendingCount || 0;
      case 'all':
        return totalElements;
      default:
        return '';
    }
  };

  // Render severity badge compact
  const renderSeverityBadge = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.INFO;
    const Icon = config.icon;
    return (
      <span
        className="severity-badge-compact"
        style={{ backgroundColor: config.bg, color: config.color }}
        title={config.label}
      >
        <Icon size={12} />
        <span className="severity-text">{config.label}</span>
      </span>
    );
  };

  // Render status badge
  const renderStatusBadge = (error) => {
    if (error.resolved) {
      return (
        <span className="status-badge-compact resolved">
          <MdCheckCircle size={12} />
          <span>Resolved</span>
        </span>
      );
    } else if (error.status === 'PENDING') {
      return (
        <span className="status-badge-compact pending">
          <MdPending size={12} />
          <span>Pending</span>
        </span>
      );
    } else {
      return (
        <span className="status-badge-compact open">
          <MdCancel size={12} />
          <span>Open</span>
        </span>
      );
    }
  };

  return (
    <div className="error-logs-page compact">
      <PageHeader
        title="Error Logs"
        subtitle="Monitor and manage system errors"
      />

      {/* Compact Stats Bar */}
      <div className="error-stats-bar">
        <div className="stat-item critical">
          <MdError size={18} />
          <span className="stat-value">
            {isDashboardLoading ? '-' : dashboardStats?.criticalUnresolvedCount || 0}
          </span>
          <span className="stat-label">Critical</span>
        </div>
        <div className="stat-item error">
          <MdBugReport size={18} />
          <span className="stat-value">
            {isDashboardLoading ? '-' : dashboardStats?.errorsLast24Hours || 0}
          </span>
          <span className="stat-label">24h Errors</span>
        </div>
        <div className="stat-item warning">
          <MdWarning size={18} />
          <span className="stat-value">
            {isDashboardLoading ? '-' : dashboardStats?.unresolvedBySeverity?.WARNING || 0}
          </span>
          <span className="stat-label">Warnings</span>
        </div>
        <div className="stat-item total">
          <MdAccessTime size={18} />
          <span className="stat-value">{totalElements}</span>
          <span className="stat-label">Total</span>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="status-tabs">
        {STATUS_TABS.map((tab) => {
          const TabIcon = tab.icon;
          const count = getTabCount(tab.key);
          return (
            <button
              key={tab.key}
              className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.key)}
            >
              <TabIcon size={16} />
              <span>{tab.label}</span>
              {count !== '' && <span className="tab-count">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Compact Actions Bar */}
      <div className="error-actions-bar compact">
        <div className="search-section">
          <div className="search-input-wrapper compact">
            <MdSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search errors..."
              value={filters.search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <button
            className={`icon-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
            title="Filters"
          >
            <MdFilterList size={18} />
          </button>
          <button
            className="icon-btn"
            onClick={() => { fetchErrorLogs(); fetchDashboardStats(); }}
            title="Refresh"
          >
            <MdRefresh size={18} />
          </button>
        </div>

        {selectedErrors.size > 0 && (
          <div className="bulk-actions compact">
            <span className="selected-count">{selectedErrors.size} selected</span>
            <button
              className="bulk-resolve-btn compact"
              onClick={() => {
                setResolvingErrorId(null);
                setShowResolveModal(true);
              }}
            >
              <MdCheckCircle size={14} />
              Resolve
            </button>
          </div>
        )}
      </div>

      {/* Compact Filters Panel */}
      {showFilters && (
        <div className="filters-panel compact">
          <div className="filter-group">
            <label>Severity</label>
            <select
              value={filters.severity}
              onChange={(e) => handleFilterChange('severity', e.target.value)}
            >
              <option value="">All</option>
              <option value="CRITICAL">Critical</option>
              <option value="ERROR">Error</option>
              <option value="WARNING">Warning</option>
              <option value="INFO">Info</option>
            </select>
          </div>

          <div className="filter-group">
            <label>From</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <button
            className="clear-filters-btn"
            onClick={() => setFilters({ severity: '', search: '', startDate: '', endDate: '' })}
          >
            Clear
          </button>
        </div>
      )}

      {/* Compact Error List */}
      <div className="error-list-container compact">
        {isLoading ? (
          <div className="loading-state compact">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : errors.length === 0 ? (
          <div className="empty-state compact">
            <MdCheckCircle size={36} className="empty-icon" />
            <h3>No errors found</h3>
            <p>Great! No errors match your current filters.</p>
          </div>
        ) : (
          <>
            <div className="error-list-header compact">
              <div className="header-checkbox">
                <input
                  type="checkbox"
                  checked={selectedErrors.size === errors.length && errors.length > 0}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="header-severity">Severity</div>
              <div className="header-message">Error</div>
              <div className="header-time">Time</div>
              <div className="header-status">Status</div>
              <div className="header-actions"></div>
            </div>

            {errors.map((error) => (
              <div key={error.id} className={`error-item compact ${expandedError === error.id ? 'expanded' : ''}`}>
                <div className="error-row compact" onClick={() => setExpandedError(expandedError === error.id ? null : error.id)}>
                  <div className="error-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedErrors.has(error.id)}
                      onChange={() => toggleErrorSelection(error.id)}
                    />
                  </div>
                  <div className="error-severity">
                    {renderSeverityBadge(error.severity)}
                  </div>
                  <div className="error-message compact">
                    <div className="error-type-text">
                      <span className="error-type">{error.errorType}</span>
                      {error.httpStatus && <span className="http-status">{error.httpStatus}</span>}
                    </div>
                    <span className="error-text">{error.message}</span>
                    {error.requestUri && (
                      <code className="error-uri-compact">{error.httpMethod} {error.requestUri}</code>
                    )}
                  </div>
                  <div className="error-time compact">
                    <span className="relative-time">{formatRelativeTime(error.timestamp)}</span>
                  </div>
                  <div className="error-status">
                    {renderStatusBadge(error)}
                  </div>
                  <div className="error-actions compact" onClick={(e) => e.stopPropagation()}>
                    {expandedError === error.id ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                  </div>
                </div>

                {expandedError === error.id && (
                  <div className="error-details compact">
                    <div className="details-grid compact">
                      <div className="detail-section">
                        <h4>Request Info</h4>
                        <div className="detail-row">
                          <span className="detail-label">URI:</span>
                          <span className="detail-value">{error.requestUri || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Method:</span>
                          <span className="detail-value">{error.httpMethod || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Status:</span>
                          <span className="detail-value">{error.httpStatus || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">IP:</span>
                          <span className="detail-value">{error.clientIp || '-'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>User Info</h4>
                        <div className="detail-row">
                          <span className="detail-label">Email:</span>
                          <span className="detail-value">{error.userEmail || 'Anonymous'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Role:</span>
                          <span className="detail-value">{error.userRole || '-'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4>Location</h4>
                        <div className="detail-row">
                          <span className="detail-label">Controller:</span>
                          <span className="detail-value">{error.controllerClass || '-'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Method:</span>
                          <span className="detail-value">{error.controllerMethod || '-'}</span>
                        </div>
                      </div>

                      {error.resolved && (
                        <div className="detail-section">
                          <h4>Resolution</h4>
                          <div className="detail-row">
                            <span className="detail-label">By:</span>
                            <span className="detail-value">{error.resolvedBy || '-'}</span>
                          </div>
                          <div className="detail-row">
                            <span className="detail-label">At:</span>
                            <span className="detail-value">{formatDateCompact(error.resolvedAt)}</span>
                          </div>
                          {error.resolutionNotes && (
                            <div className="detail-row">
                              <span className="detail-label">Notes:</span>
                              <span className="detail-value">{error.resolutionNotes}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {error.stackTrace && (
                      <div className="stack-trace-section compact">
                        <div className="stack-trace-header">
                          <h4>Stack Trace</h4>
                          <button
                            className="copy-btn"
                            onClick={() => copyStackTrace(error.stackTrace)}
                          >
                            <MdContentCopy size={14} />
                            Copy
                          </button>
                        </div>
                        <pre className="stack-trace compact">{error.stackTrace}</pre>
                      </div>
                    )}

                    <div className="error-detail-actions compact">
                      {!error.resolved ? (
                        <button
                          className="resolve-btn compact"
                          onClick={() => {
                            setResolvingErrorId(error.id);
                            setSelectedErrors(new Set());
                            setShowResolveModal(true);
                          }}
                        >
                          <MdDone size={14} />
                          Resolve
                        </button>
                      ) : (
                        <button
                          className="reopen-btn compact"
                          onClick={() => handleReopen(error.id)}
                        >
                          <MdReplay size={14} />
                          Reopen
                        </button>
                      )}
                      <button
                        className="delete-btn compact"
                        onClick={() => {
                          setDeletingErrorId(error.id);
                          setShowDeleteModal(true);
                        }}
                      >
                        <MdDelete size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage + 1}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page - 1)}
        />
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
          <div className="modal-content compact" onClick={(e) => e.stopPropagation()}>
            <h3>Resolve Error{selectedErrors.size > 1 ? 's' : ''}</h3>
            <p>
              {selectedErrors.size > 1
                ? `Mark ${selectedErrors.size} errors as resolved?`
                : 'Add optional resolution notes:'}
            </p>
            <textarea
              value={resolveNotes}
              onChange={(e) => setResolveNotes(e.target.value)}
              placeholder="Resolution notes (optional)..."
              rows={3}
            />
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowResolveModal(false)}>
                Cancel
              </button>
              <button className="confirm-btn" onClick={handleResolve}>
                <MdCheckCircle size={14} />
                Resolve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content compact" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Error Log</h3>
            <p>Are you sure you want to permanently delete this error log?</p>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button className="delete-confirm-btn" onClick={handleDelete}>
                <MdDelete size={14} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorLogs;
